import { GoalOverviewRoadmapViewOptions } from '@griply/types';
import { getFilterDocumentGoalTypes, getWeekStartsOn } from '@griply/utils';
import { useGoalsTimelineFilterDocument } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TimelinePremiumLock } from 'shared/components/connected/timeline-premium-lock';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useUser } from 'shared/hooks/use-user';
import { LimitKeys } from 'shared/types/limit-keys';

import { GroupByList } from './components/group-by-list';
import { MobileAddGoalButton } from './components/mobile-add-goal-button';
import { PageTopBar } from './components/page-top-bar';
import { Timeline } from './components/timeline';
import * as Styled from './goals-overview-calendar.style';
import { useCalendarControls } from './hooks/use-calendar-controls';
import { useFilteredLocalizedLifeAreas } from './hooks/use-filtered-localized-life-areas';
import { useGoalsOverviewFilteredGoals } from './hooks/use-goals-overview-filtered-goals';

export type GoalsOverviewCalendarProps = {
  view: GoalOverviewRoadmapViewOptions;
};

export const GoalsOverviewCalendar: React.FC<GoalsOverviewCalendarProps> = ({
  view,
}) => {
  const { t } = useTranslation();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const isTimeline = view === GoalOverviewRoadmapViewOptions.Timeline;
  const { allowed: isTimelineAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopGoalsOverviewTimeline,
    {},
  );
  const showTimeline = isTimeline && isTimelineAllowed;

  const {
    timeframe,
    referenceDate,
    scrollRequest,
    onTimeframe,
    onPrev,
    onToday,
    onNext,
  } = useCalendarControls(showTimeline);

  const timelineFilterDocument = useGoalsTimelineFilterDocument();
  const timelineGoalTypes = useMemo(
    () => getFilterDocumentGoalTypes(timelineFilterDocument),
    [timelineFilterDocument],
  );
  const timelineGoals = useGoalsOverviewFilteredGoals({
    filterDocument: timelineFilterDocument,
    showCompleted:
      user?.settings?.goalsOverview?.timeline?.showCompletedGoals ?? true,
    goalTypes: timelineGoalTypes,
  });
  const lifeAreas = useFilteredLocalizedLifeAreas(timelineGoals);

  const title = t(isTimeline ? 'pages.timeline.title' : 'pages.goals.title');

  return (
    <Styled.Container>
      <Styled.Main>
        <Styled.MainContent>
          <PageTopBar
            view={view}
            title={title}
            showTimeframeControls={showTimeline}
            timeframe={timeframe}
            onTimeframe={onTimeframe}
            onPrev={onPrev}
            onToday={onToday}
            onNext={onNext}
          />

          {!isTimeline && <GroupByList />}

          {isTimeline &&
            (isTimelineAllowed ? (
              <Timeline
                timeframe={timeframe}
                referenceDate={referenceDate}
                scrollRequest={scrollRequest}
                goals={timelineGoals}
                lifeAreas={lifeAreas}
                weekStartsOn={weekStartsOn}
              />
            ) : (
              <TimelinePremiumLock
                title={t('pages.goals.roadmap.timeline.title')}
              />
            ))}
        </Styled.MainContent>
      </Styled.Main>

      <MobileAddGoalButton />
    </Styled.Container>
  );
};
